import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from 'notistack';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { AdminTemplate, TemplateType, getAdminTemplates } from '../../../services/admin-service';

const templateTypeLabel: Record<TemplateType, string> = {
  image: 'Imagem',
  html: 'E-mail',
  text: 'Texto',
};

const templateTypeColor: Record<TemplateType, 'success' | 'info' | 'secondary'> = {
  image: 'success',
  html: 'info',
  text: 'secondary',
};

const AdminTemplatesPage: React.FC = () => {
  const { setBreadcrumbs, setBreadcrumbActions } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<AdminTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Painel', path: '/admin' },
      { label: 'Templates', path: '/admin/templates' },
    ]);

    setBreadcrumbActions(
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => navigate('/admin/templates/new')}
        sx={{
          backgroundColor: '#83b81a',
          color: 'black',
          '&:hover': {
            backgroundColor: '#5b8012',
          },
        }}
      >
        Novo Template
      </Button>
    );

    return () => {
      setBreadcrumbActions(null);
    };
  }, [setBreadcrumbs, setBreadcrumbActions, navigate]);

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const response = await getAdminTemplates(page + 1, rowsPerPage);

        if (response && Array.isArray(response.items)) {
          setTemplates(response.items);
          setTotalItems(response.total);
        } else {
          setTemplates([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        enqueueSnackbar('Falha ao carregar templates', { variant: 'error' });
        setTemplates([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [page, rowsPerPage, enqueueSnackbar]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Autor</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="center">Tipo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.length > 0 ? (
                  templates.map(template => (
                    <TableRow
                      key={template.id}
                      hover
                      onClick={() => navigate(`/admin/templates/${template.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2">{template.title}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '520px',
                          }}
                        >
                          {template.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{template.author}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{template.author_email}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={
                            templateTypeLabel[template.template_type] || template.template_type
                          }
                          color={templateTypeColor[template.template_type] || 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Nenhum template encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </>
      )}
    </Box>
  );
};

export default AdminTemplatesPage;
